import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CheckBox from 'shared/components/CheckBox';
import './StoreFilter.scss';

const StoreFilter = ({ matches, onFilterChange }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);

  // Extract clean domain name from source
  const extractDomainName = source => {
    if (!source) return 'Unknown';

    try {
      // Handle cases where source might be a full URL or just domain
      let domain = source;

      // Remove protocol if present
      if (domain.includes('://')) {
        domain = domain.split('://')[1];
      }

      // Remove www. prefix
      domain = domain.replace(/^www\./, '');

      // Take only the domain part (before first slash)
      domain = domain.split('/')[0];

      // Remove port if present
      domain = domain.split(':')[0];

      // Capitalize first letter for display
      return domain.charAt(0).toUpperCase() + domain.slice(1);
    } catch (error) {
      return source || 'Unknown';
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isExpanded]);

  // Extract unique stores from all matches
  const uniqueStores = useMemo(() => {
    const storeMap = new Map();

    matches.forEach(match => {
      if (match.source) {
        const domain = extractDomainName(match.source);
        if (!storeMap.has(domain)) {
          storeMap.set(domain, {
            domain,
            displayName: domain,
            icon: match.source_icon,
            count: 1,
          });
        } else {
          storeMap.get(domain).count++;
        }
      }
    });

    // Sort by count (most matches first), then alphabetically
    return Array.from(storeMap.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [matches]);

  const [selectedStores, setSelectedStores] = useState(new Set(uniqueStores.map(store => store.domain)));

  // Reset selected stores when uniqueStores change (different matches)
  useEffect(() => {
    const newStores = new Set(uniqueStores.map(store => store.domain));
    setSelectedStores(newStores);
  }, [uniqueStores.length]); // Only reset when store count changes

  const handleStoreToggle = (storeDomain, isChecked) => {
    const newSelectedStores = new Set(selectedStores);

    if (isChecked && !selectedStores.has(storeDomain)) {
      newSelectedStores.add(storeDomain);
    } else if (!isChecked && selectedStores.has(storeDomain)) {
      newSelectedStores.delete(storeDomain);
    }

    setSelectedStores(newSelectedStores);

    // Pass the selected stores Set to parent instead of filtered matches
    onFilterChange(newSelectedStores);
  };

  const handleSelectAllToggle = isChecked => {
    if (isChecked) {
      const allStores = new Set(uniqueStores.map(store => store.domain));
      setSelectedStores(allStores);
      onFilterChange(allStores);
    } else {
      setSelectedStores(new Set());
      onFilterChange(new Set());
    }
  };

  const isAllSelected = selectedStores.size === uniqueStores.length;
  const isPartiallySelected = selectedStores.size > 0 && selectedStores.size < uniqueStores.length;
  const isFilterActive = !isAllSelected;

  // Handle indeterminate state for select all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  if (uniqueStores.length <= 1) {
    return null; // Don't show filter if there's only one store or no stores
  }

  return (
    <div className='store-filter' ref={dropdownRef}>
      {/* Filter Toggle Button */}
      <button
        className={`btn btn-sm btn-outline-dark d-flex align-items-center px-8 py-2 ${isExpanded ? 'active' : ''} ${
          isFilterActive ? 'filter-active' : ''
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Custom Store Icons Stack */}
        <div className='store-icons-stack mr-6'>
          {uniqueStores.slice(0, 3).map((store, index) => (
            <div
              key={store.domain}
              className='store-icon-mini'
              style={{
                position: index > 0 ? 'absolute' : 'relative',
                left: index > 0 ? `${index * 8}px` : '0',
                zIndex: 3 - index,
              }}
            >
              {store.icon ? (
                <img
                  src={store.icon}
                  alt={store.displayName}
                  className='store-icon-img'
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className='store-icon-placeholder'>{store.displayName.charAt(0)}</div>
              )}
            </div>
          ))}
          {uniqueStores.length > 3 && (
            <div
              className='store-icon-more'
              style={{
                position: 'absolute',
                left: '24px',
                zIndex: 0,
              }}
            >
              +{uniqueStores.length - 3}
            </div>
          )}
        </div>
        <span>{t('filter_stores', 'Filter Stores')}</span>
        {isFilterActive && (
          <span className='filter-indicator ml-5'>
            <span className='badge badge-primary badge-sm'>{selectedStores.size}</span>
          </span>
        )}
      </button>

      {/* Filter Dropdown */}
      {isExpanded && (
        <div className='store-filter-dropdown position-absolute bg-white border rounded shadow-lg p-10 mt-2 z-index-100'>
          <div className='filter-header mb-8 pb-6 border-bottom'>
            <div className='d-flex align-items-center justify-content-between mb-6'>
              <h6 className='mb-0 fw-600'>{t('filter_by_store', 'Filter by Store')}</h6>
              <button className='btn btn-sm btn-primary px-12 py-4' onClick={() => setIsExpanded(false)}>
                {t('btn_done', 'Done')}
              </button>
            </div>
            {/* Select All Checkbox */}
            <div className='store-item d-flex align-items-center mb-0'>
              <CheckBox
                className='mb-0 flex-grow-1'
                label={t('select_all', 'Select All')}
                id='select-all-stores'
                name='select-all-stores'
                checked={isAllSelected}
                ref={selectAllCheckboxRef}
                onChange={e => handleSelectAllToggle(e.target.checked)}
              />
            </div>
          </div>

          <div className='store-list max-h-250 overflow-auto'>
            {uniqueStores.map(store => (
              <div key={store.domain} className='store-item d-flex align-items-center mb-6'>
                <CheckBox
                  className='mb-0 flex-grow-1'
                  label={
                    <div className='d-flex align-items-center'>
                      {store.icon && (
                        <img
                          src={store.icon}
                          alt={store.displayName}
                          className='store-icon mr-8'
                          style={{
                            width: '16px',
                            height: '16px',
                            objectFit: 'contain',
                            borderRadius: '2px',
                          }}
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className='store-info'>
                        <span className='store-name fw-500'>{store.displayName}</span>
                        <small className='store-count text-muted ml-5'>({store.count})</small>
                      </div>
                    </div>
                  }
                  id={`store-${store.domain}`}
                  name={`store-${store.domain}`}
                  checked={selectedStores.has(store.domain)}
                  onChange={e => handleStoreToggle(store.domain, e.target.checked)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreFilter;
